'use client';

import { c as _c } from "react/compiler-runtime";
import { UserType } from '@shared/all/domains/user';
import { signupSchema, usernameSchema } from '@shared/all/validators';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import toast from '@shared/utils/helpers/toast';
import useAuth from '@shared/utils/hooks/useAuth';
import usePasskey from '@shared/utils/hooks/usePasskey';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, Loader2, Lock, Shuffle } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useContext, useReducer, useRef, useState } from 'react';
import { useFinishOAuth } from '../../hooks/useFinishOAuth';
import useGuideStorage from '../../hooks/useGuideStorage';
import { spaceConfig } from '../../utils/getSpaceConfig';
import { client, service } from '../../utils/trpc';
import Button from '../common/Button';
import Input from '../UI/Input';
import PasswordInput from '../UI/PasswordInput';
import { ToggleItem } from '../UI/ToggleItem';
import AuthScriptProviders from './AuthScriptProviders';
import { LOGIN_HANDOFF_KEY } from './loginHandoff';
import OAuthButtons from './OAuthButtons';
import { RecaptchaCheckbox, type RecaptchaCheckboxRef } from './RecaptchaCheckbox';
interface Props {
  closeHandler: () => void;
  dividerBackground?: 'primary' | 'secondary' | 'light';
}
type FormState = {
  privateUser: boolean;
  showPassword: boolean;
  submitting: boolean;
};
type FormAction = {
  type: 'togglePrivate';
} | {
  type: 'revealPassword';
} | {
  type: 'hidePassword';
} | {
  type: 'submitting';
  value: boolean;
};
function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'togglePrivate':
      return {
        ...state,
        privateUser: !state.privateUser
      };
    case 'revealPassword':
      return {
        ...state,
        showPassword: true
      };
    case 'hidePassword':
      return {
        ...state,
        showPassword: false
      };
    case 'submitting':
      return {
        ...state,
        submitting: action.value
      };
  }
}
export const Signup = t0 => {
  const $ = _c(89);
  const {
    closeHandler,
    dividerBackground: t1
  } = t0;
  const dividerBackground = t1 === undefined ? "secondary" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      client,
      service
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    initializeUser
  } = useAuth(t2);
  const {
    openLoginModal,
    openPostSignup
  } = useContext(AuthWidgetContext);
  const {
    syncViewedGuidesToServer
  } = useGuideStorage();
  const finishOAuth = useFinishOAuth();
  const {
    signUpWithPasskey,
    isPasskeySupported,
    busy: passkeyBusy
  } = usePasskey(client);
  const [typedUsername, setTypedUsername] = useState(null);
  const [password, setPassword] = useState("");
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      privateUser: false,
      showPassword: false,
      submitting: false
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [form, dispatch] = useReducer(formReducer, t3);
  const recaptchaRef = useRef(null);
  const usernameRef = useRef(null);
  const query = useSearchParams();
  let t4;
  if ($[2] !== query) {
    t4 = query?.get("referrer") ? parseInt(query.get("referrer")) : undefined;
    $[2] = query;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const referrerUserId = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      queryKey: ["suggestUsername"],
      queryFn: _temp,
      staleTime: Infinity
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const {
    data: suggestedUsername
  } = useQuery(t5);
  const username = typedUsername ?? suggestedUsername ?? "";
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["passkeySupported"];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== isPasskeySupported) {
    t7 = {
      queryKey: t6,
      queryFn: isPasskeySupported,
      staleTime: Infinity
    };
    $[6] = isPasskeySupported;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const {
    data: t8
  } = useQuery(t7);
  const passkeySupported = t8 === undefined ? true : t8;
  let t9;
  if ($[8] !== username) {
    t9 = {
      queryKey: ["usernameAvailability", username],
      queryFn: async () => {
        if (!usernameSchema.safeParse(username).success) {
          return false;
        }
        const [error_0, response] = await client.users.isUsernameAvailable.query({
          username
        });
        if (error_0) {
          return false;
        }
        return response;
      },
      placeholderData: _temp2
    };
    $[8] = username;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const {
    data: usernameAvailable,
    isLoading: usernameChecking
  } = useQuery(t9);
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = async () => {
      const [error_1, name_0] = await client.users.suggestUsername.query();
      if (!error_1 && name_0) {
        setTypedUsername(name_0);
      }
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const shuffleUsername = t10;
  const usernameIsSuggestion = typedUsername === null && username !== "";
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      setTypedUsername("");
      usernameRef.current?.focus();
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const chooseOwnUsername = t11;
  let t12;
  if ($[12] !== closeHandler || $[13] !== initializeUser || $[14] !== openPostSignup || $[15] !== syncViewedGuidesToServer) {
    t12 = async () => {
      localStorage.removeItem("basicGuideCompleted");
      await syncViewedGuidesToServer();
      await client.onboarding.trackEvent.mutate({
        type: "TOUR_COMPLETED",
        meta: "activation"
      });
      await initializeUser();
      closeHandler();
      openPostSignup();
    };
    $[12] = closeHandler;
    $[13] = initializeUser;
    $[14] = openPostSignup;
    $[15] = syncViewedGuidesToServer;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const finishSignup = t12;
  let t13;
  if ($[17] !== username || $[18] !== usernameAvailable) {
    t13 = () => {
      if (!usernameSchema.safeParse(username).success) {
        toast("Please choose a valid username.", "", "error");
        return false;
      }
      if (usernameAvailable !== true) {
        toast("That username is taken. Try another.", "", "error");
        return false;
      }
      return true;
    };
    $[17] = username;
    $[18] = usernameAvailable;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const ensureUsernameReady = t13;
  let t14;
  if ($[20] !== form.privateUser) {
    t14 = form.privateUser ? [UserType.PRIVATE] : [];
    $[20] = form.privateUser;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const types = t14;
  let t15;
  if ($[22] !== ensureUsernameReady || $[23] !== finishSignup || $[24] !== referrerUserId || $[25] !== signUpWithPasskey || $[26] !== types || $[27] !== username) {
    t15 = async () => {
      if (!ensureUsernameReady()) {
        return;
      }
      const ok = await signUpWithPasskey({
        username,
        instanceId: spaceConfig.space.id,
        referrerUserId,
        types
      });
      if (ok) {
        await finishSignup();
      }
    };
    $[22] = ensureUsernameReady;
    $[23] = finishSignup;
    $[24] = referrerUserId;
    $[25] = signUpWithPasskey;
    $[26] = types;
    $[27] = username;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const handlePasskeySignup = t15;
  let t16;
  if ($[29] !== ensureUsernameReady || $[30] !== finishSignup || $[31] !== password || $[32] !== referrerUserId || $[33] !== types || $[34] !== username) {
    t16 = async e => {
      e.preventDefault();
      if (!ensureUsernameReady()) {
        return;
      }
      if (!signupSchema.shape.password.safeParse(password).success) {
        toast("Password must be at least 8 characters.", "", "error");
        return;
      }
      const recaptchaToken = recaptchaRef.current?.getToken();
      if (!recaptchaToken) {
        toast("Please complete the \"I'm not a robot\" check.", "", "error");
        return;
      }
      dispatch({
        type: "submitting",
        value: true
      });
      const [error_2] = await client.authPassword.register.mutate({
        username,
        password,
        types,
        instanceId: spaceConfig.space.id,
        referrerUserId,
        recaptchaToken,
        platform: "web"
      });
      if (error_2) {
        toast(error_2.message || "Signup failed. Please try again.", "", "error");
        dispatch({
          type: "submitting",
          value: false
        });
        return;
      }
      await finishSignup();
    };
    $[29] = ensureUsernameReady;
    $[30] = finishSignup;
    $[31] = password;
    $[32] = referrerUserId;
    $[33] = types;
    $[34] = username;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const handlePasswordSubmit = t16;
  let t17;
  if ($[36] !== closeHandler || $[37] !== openLoginModal) {
    t17 = () => {
      openLoginModal();
      closeHandler();
    };
    $[36] = closeHandler;
    $[37] = openLoginModal;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const openLoginModalHandler = t17;
  let t18;
  if ($[39] !== closeHandler || $[40] !== openLoginModal || $[41] !== username) {
    t18 = () => {
      window.sessionStorage.setItem(LOGIN_HANDOFF_KEY, username);
      openLoginModal();
      closeHandler();
    };
    $[39] = closeHandler;
    $[40] = openLoginModal;
    $[41] = username;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const signInAsTypedUsername = t18;
  const usernameTaken = username !== "" && usernameAvailable === false;
  let t19;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <h1 className="mb-8 text-3xl font-emphasis">Join {spaceConfig.space.slug}</h1>;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <button key="shuffle-username" type="button" onClick={shuffleUsername} title="Generate a new username" className="cursor-pointer text-textSecondary hover:text-textPrimary"><Shuffle size={18} /></button>;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== usernameAvailable || $[46] !== usernameChecking) {
    t21 = [t20, usernameChecking ? <Loader2 className="animate-spin" size={20} key="username-loading-icon" /> : <CheckCircle2 key="username-valid-icon" size={20} color={usernameAvailable ? "green" : "red"} />];
    $[45] = usernameAvailable;
    $[46] = usernameChecking;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t21 || $[49] !== username) {
    t22 = <Input ref={usernameRef} type="text" label="Username" value={username} onChange={setTypedUsername} icons={t21} />;
    $[48] = t21;
    $[49] = username;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== usernameIsSuggestion) {
    t23 = usernameIsSuggestion && <p className="text-sm text-textSecondary">We picked this name for you. Use it, or{" "}<button type="button" className="text-primary hover:underline" onClick={chooseOwnUsername}>choose your own</button>.</p>;
    $[51] = usernameIsSuggestion;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== signInAsTypedUsername || $[54] !== username || $[55] !== usernameTaken) {
    t24 = usernameTaken && <p className="text-sm text-destructive">{username} already has an account.{" "}<button type="button" className="text-primary underline" onClick={signInAsTypedUsername}>Sign in as {username}</button></p>;
    $[53] = signInAsTypedUsername;
    $[54] = username;
    $[55] = usernameTaken;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t22 || $[58] !== t23 || $[59] !== t24) {
    t25 = <div className="w-full space-y-1">{t22}{t23}{t24}</div>;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => dispatch({
      type: "togglePrivate"
    });
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== form.privateUser) {
    t27 = <div className="mt-4 w-full"><ToggleItem title="Enable Private Mode" description="Hide from searches and recommendations." state={form.privateUser} onChange={t26} /></div>;
    $[62] = form.privateUser;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== form.showPassword || $[65] !== form.submitting || $[66] !== handlePasswordSubmit || $[67] !== password) {
    t28 = form.showPassword && <div className="mt-4 w-full"><form className="space-y-2" onSubmit={handlePasswordSubmit}><PasswordInput value={password} onChange={setPassword} icons={[<CheckCircle2 size={20} key="password-valid-icon" color={signupSchema.shape.password.safeParse(password).success ? "green" : "red"} />]} /><div className="mt-2 flex justify-center"><RecaptchaCheckbox ref={recaptchaRef} /></div><Button text="Create account" type="submit" classNames="mt-2 block w-full" loading={form.submitting} /><button type="button" className="mt-1 w-full text-sm text-textSecondary hover:text-textPrimary" onClick={() => dispatch({
          type: "hidePassword"
        })}>Use another method</button></form></div>;
    $[64] = form.showPassword;
    $[65] = form.submitting;
    $[66] = handlePasswordSubmit;
    $[67] = password;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== closeHandler || $[70] !== dividerBackground || $[71] !== finishOAuth || $[72] !== form.privateUser || $[73] !== form.showPassword || $[74] !== handlePasskeySignup || $[75] !== passkeyBusy || $[76] !== passkeySupported) {
    t29 = !form.showPassword && <><div className="mt-6 w-full space-y-3">{passkeySupported && <Button variant="SECONDARY" text="Continue with a passkey" icon={<KeyRound size={18} />} type="button" borderTransparent={false} classNames="w-full" loading={passkeyBusy} onClick={handlePasskeySignup} />}<Button variant="SECONDARY" text="Sign up with a password" icon={<Lock size={18} />} type="button" borderTransparent={false} classNames="w-full" onClick={() => dispatch({
          type: "revealPassword"
        })} /></div><div className="mt-3 w-full"><OAuthButtons onSuccess={() => finishOAuth(closeHandler)} isPrivate={form.privateUser} dividerBackground={dividerBackground} showDivider={false} /></div></>;
    $[69] = closeHandler;
    $[70] = dividerBackground;
    $[71] = finishOAuth;
    $[72] = form.privateUser;
    $[73] = form.showPassword;
    $[74] = handlePasskeySignup;
    $[75] = passkeyBusy;
    $[76] = passkeySupported;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  let t30;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Link className="text-textGray underline" href="/terms">Terms</Link>;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  let t31;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <p className="mx-auto mt-3 self-start text-sm text-textGray">By signing up, I agree to {spaceConfig.space.slug}'s{" "}{t30}{" "}and{" "}<Link className="text-textGray underline" href="/privacy">Privacy Policy</Link>.</p>;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== openLoginModalHandler) {
    t32 = <p className="mx-auto mt-6">Already have an account?{" "}<span className="cursor-pointer underline" onClick={openLoginModalHandler}>Sign in</span></p>;
    $[80] = openLoginModalHandler;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <p className="mx-auto mt-3 self-start text-sm text-textGray">Your Factiii login also works on{" "}<a className="text-textGray underline" href="https://oakbox.me" target="_blank" rel="noopener noreferrer">Oakbox</a>.</p>;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t25 || $[84] !== t27 || $[85] !== t28 || $[86] !== t29 || $[87] !== t32) {
    t34 = <AuthScriptProviders><div className="mx-auto flex size-full max-w-sm flex-col items-center justify-center">{t19}{t25}{t27}{t28}{t29}{t31}{t32}{t33}</div></AuthScriptProviders>;
    $[83] = t25;
    $[84] = t27;
    $[85] = t28;
    $[86] = t29;
    $[87] = t32;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  return t34;
};
async function _temp() {
  const [error, name] = await client.users.suggestUsername.query();
  return error ? "" : name ?? "";
}
function _temp2(previousData) {
  return previousData;
}